import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {toCdnUrl} from '../../../utils/cdnUrl'
import {
  getMarketplaceStats,
  getFailedListings,
  retryAllFailed,
  retrySingleListing,
  MarketplaceAccountStats,
  MarketplaceSummary,
  FailedListing,
} from './core/_requests'

const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const MarketplacePage: React.FC = () => {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<MarketplaceSummary | null>(null)
  const [accounts, setAccounts] = useState<MarketplaceAccountStats[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [failedListings, setFailedListings] = useState<FailedListing[]>([])
  const [retrying, setRetrying] = useState(false)
  const [retryingOne, setRetryingOne] = useState<Record<string, 'loading' | 'success' | 'error'>>({})
  const [retryResult, setRetryResult] = useState<{retried: number; errors: string[]} | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [statsRes, failedRes] = await Promise.all([
        getMarketplaceStats(),
        getFailedListings(),
      ])
      setSummary(statsRes.data.summary)
      setAccounts(statsRes.data.accounts)
      setFailedListings(failedRes.data.listings || [])
    } catch (error) {
      console.error('Failed to load marketplace stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRetryAll = async () => {
    if (!window.confirm(`לנסות שוב ${failedListings.length} מודעות שנכשלו?`)) return
    setRetrying(true)
    setRetryResult(null)
    try {
      const res = await retryAllFailed()
      setRetryResult({retried: res.data.retried, errors: res.data.errors || []})
      // Refresh data
      setTimeout(() => loadData(), 2000)
    } catch (error) {
      console.error('Failed to retry:', error)
      setRetryResult({retried: 0, errors: ['שגיאה בשרת']})
    } finally {
      setRetrying(false)
    }
  }

  const handleRetrySingle = async (listingId: string) => {
    setRetryingOne(prev => ({...prev, [listingId]: 'loading'}))
    try {
      await retrySingleListing(listingId)
      setRetryingOne(prev => ({...prev, [listingId]: 'success'}))
    } catch {
      setRetryingOne(prev => ({...prev, [listingId]: 'error'}))
    }
  }

  const filteredAccounts = accounts.filter((acc) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      (acc.email || '').toLowerCase().includes(q) ||
      (acc.name || '').toLowerCase().includes(q) ||
      (acc.company_name || '').toLowerCase().includes(q)
    )
  })

  const statsData = [
    {
      label: 'חשבונות עם מודעות',
      value: summary?.total_accounts || 0,
      icon: 'bi-people-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'סה"כ מודעות',
      value: summary?.total_listings || 0,
      icon: 'bi-shop-window',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      label: 'מודעות פעילות',
      value: summary?.total_live || 0,
      icon: 'bi-lightning-charge-fill',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
      label: 'נמכרו',
      value: summary?.total_sold || 0,
      icon: 'bi-bag-check-fill',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
  ]

  return (
    <div style={{padding: 0}}>
      {/* Page Header */}
      <div style={{marginBottom: '32px'}}>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: colors.textPrimary,
            margin: 0,
          }}
        >
          מרקטפלייס
        </h1>
        <p style={{color: colors.textSecondary, marginTop: '8px', fontSize: '14px'}}>
          סטטיסטיקות מודעות לפי חשבון
        </p>
      </div>

      {/* E2EE "restore chats" modal warning — surfaced when the bot detected
          FB's encrypted-restore popup during a messenger auto-reply. The bot
          auto-dismisses it and still sends, but if it keeps reappearing an
          admin should log into that profile and clear it manually. */}
      {(() => {
        const RECENT_MS = 48 * 60 * 60 * 1000
        const flagged = accounts
          .filter(
            (a) =>
              a.marketplace_e2ee_modal_seen_at &&
              Date.now() - new Date(a.marketplace_e2ee_modal_seen_at).getTime() < RECENT_MS
          )
          .sort(
            (a, b) =>
              new Date(b.marketplace_e2ee_modal_seen_at as string).getTime() -
              new Date(a.marketplace_e2ee_modal_seen_at as string).getTime()
          )
        if (!flagged.length) return null
        return (
          <div
            style={{
              background: '#fff7ed',
              border: '1px solid #fdba74',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              color: '#7c2d12',
              fontSize: '14px',
            }}
          >
            <div style={{fontWeight: 700, marginBottom: '6px'}}>
              ⚠️ זוהתה חלונית "שחזור צ'אטים מוצפנים" (E2EE) במענה האוטומטי במסנג'ר
            </div>
            <div style={{marginBottom: '10px'}}>
              הבוט דוחה אותה אוטומטית וההודעה עדיין נשלחה, אך אם היא ממשיכה להופיע יש להתחבר
              ידנית לפרופיל ולסגור אותה. חשבונות מושפעים (48 שעות אחרונות):
            </div>
            <ul style={{margin: 0, paddingInlineStart: '20px'}}>
              {flagged.map((a) => (
                <li key={a.account_id} style={{marginBottom: '2px'}}>
                  <strong>{a.name?.trim() || a.email || a.account_id}</strong>
                  {' — '}
                  {new Date(a.marketplace_e2ee_modal_seen_at as string).toLocaleString('he-IL')}
                </li>
              ))}
            </ul>
          </div>
        )
      })()}

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {statsData.map((stat, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '20px',
              transition: 'all 0.2s ease',
              transform: hoveredCard === i ? 'translateY(-2px)' : 'none',
              border: `1px solid ${hoveredCard === i ? colors.borderColor : 'transparent'}`,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}
            >
              <i className={`bi ${stat.icon}`} style={{fontSize: '20px', color: '#fff'}}></i>
            </div>
            <div
              style={{
                fontSize: '28px',
                fontWeight: 700,
                color: colors.textPrimary,
                marginBottom: '4px',
              }}
            >
              {stat.value.toLocaleString()}
            </div>
            <div style={{fontSize: '13px', color: colors.textSecondary}}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Extra summary row */}
      {summary && (
        <div
          style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '20px',
            padding: '12px 16px',
            background: colors.bgTertiary,
            borderRadius: '8px',
            fontSize: '13px',
            color: colors.textSecondary,
            alignItems: 'center',
          }}
        >
          <span>
            ממתינות: <strong style={{color: colors.textPrimary}}>{summary.total_pending.toLocaleString()}</strong>
          </span>
          <span>
            צפיות: <strong style={{color: colors.textPrimary}}>{summary.total_views.toLocaleString()}</strong>
          </span>
          <span>
            הודעות: <strong style={{color: colors.textPrimary}}>{summary.total_messages.toLocaleString()}</strong>
          </span>
          {failedListings.length > 0 && (() => {
            const uniqueAccounts = new Set(failedListings.map(l => l.account_id)).size;
            return (
              <>
                <span style={{borderRight: `1px solid ${colors.borderColor}`, height: '20px'}} />
                <span style={{color: colors.accentDanger}}>
                  נכשלו: <strong>{failedListings.length}</strong> מודעות מ-<strong>{uniqueAccounts}</strong> חשבונות
                </span>
                <button
                  onClick={handleRetryAll}
                  disabled={retrying}
                  style={{
                    padding: '4px 14px',
                    background: retrying ? colors.bgTertiary : colors.accentDanger,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: retrying ? 'not-allowed' : 'pointer',
                    marginRight: 'auto',
                  }}
                >
                  {retrying ? 'מנסה שוב...' : `נסה שוב הכל (${failedListings.length})`}
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Search Bar */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '20px',
          alignItems: 'center',
          background: colors.bgTertiary,
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <div style={{flex: 1, position: 'relative'}}>
          <i
            className='bi bi-search'
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.textMuted,
              fontSize: '16px',
            }}
          ></i>
          <input
            type='text'
            placeholder='חיפוש לפי שם, אימייל, חברה...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 44px 12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: colors.textPrimary,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
        <div style={{fontSize: '13px', color: colors.textSecondary}}>
          {filteredAccounts.length} חשבונות
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: colors.bgTertiary,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2.5fr 1fr 1fr 1fr 1fr 1fr',
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          {['חשבון', 'מודעות', 'פעילות', 'נמכרו', 'פרופילים', 'צפיות'].map((header, i) => (
            <div
              key={i}
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: colors.textMuted,
              }}
            >
              {header}
            </div>
          ))}
        </div>

        {/* Table Body */}
        {loading ? (
          <div
            style={{
              padding: '60px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${colors.bgHover}`,
                borderTopColor: colors.accentPrimary,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            ></div>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div style={{padding: '60px', textAlign: 'center'}}>
            <i
              className='bi bi-inbox'
              style={{fontSize: '48px', color: colors.textMuted, marginBottom: '16px', display: 'block'}}
            ></i>
            <div style={{fontSize: '18px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px'}}>
              לא נמצאו חשבונות
            </div>
            <div style={{fontSize: '14px', color: colors.textSecondary}}>נסה לשנות את פרמטרי החיפוש</div>
          </div>
        ) : (
          filteredAccounts.map((account) => {
            const displayName = account.name?.trim() || account.email || 'Unknown'

            return (
              <div
                key={account.account_id}
                onClick={() => navigate(`/admin/accounts/${account.account_id}`)}
                onMouseEnter={() => setHoveredRow(account.account_id)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2.5fr 1fr 1fr 1fr 1fr 1fr',
                  padding: '16px 24px',
                  alignItems: 'center',
                  borderBottom: `1px solid rgba(0,0,0,0.05)`,
                  cursor: 'pointer',
                  background: hoveredRow === account.account_id ? colors.bgHover : 'transparent',
                  transition: 'background 0.15s ease',
                }}
              >
                {/* Account Info */}
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#fff',
                    }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontWeight: 600, color: colors.textPrimary, fontSize: '14px'}}>
                      {displayName}
                    </div>
                    <div style={{fontSize: '12px', color: colors.textSecondary, marginTop: '2px'}}>
                      {account.email}
                    </div>
                    {account.company_name && (
                      <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '1px'}}>
                        {account.company_name}
                      </div>
                    )}
                  </div>
                  {account.is_locked && (
                    <span
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: 'rgba(246, 78, 96, 0.15)',
                        color: colors.accentDanger,
                        fontWeight: 600,
                      }}
                    >
                      נעול
                    </span>
                  )}
                </div>

                {/* Listing Count */}
                <div style={{fontWeight: 700, color: colors.textPrimary, fontSize: '16px'}}>
                  {account.listing_count.toLocaleString()}
                </div>

                {/* Live Count */}
                <div>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: 'rgba(27, 197, 189, 0.15)',
                      color: colors.accentSuccess,
                    }}
                  >
                    {account.live_count}
                  </span>
                </div>

                {/* Sold Count */}
                <div>
                  {account.sold_count > 0 ? (
                    <span
                      style={{
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: 'rgba(240, 147, 251, 0.15)',
                        color: '#b44cd8',
                      }}
                    >
                      {account.sold_count}
                    </span>
                  ) : (
                    <span style={{color: colors.textMuted}}>—</span>
                  )}
                </div>

                {/* Profiles */}
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontWeight: 600, color: colors.textPrimary, fontSize: '14px'}}>
                    {account.listing_profile_count}
                  </span>
                  <span style={{color: colors.textMuted, fontSize: '11px'}}>
                    / {account.total_profile_count}
                  </span>
                </div>

                {/* Views */}
                <div style={{color: colors.textSecondary, fontSize: '13px'}}>
                  {account.total_views > 0 ? account.total_views.toLocaleString() : '—'}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Failed Listings Section */}
      {failedListings.length > 0 && (
        <div style={{marginTop: '32px'}}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <h2 style={{fontSize: '18px', fontWeight: 600, color: colors.accentDanger, margin: 0}}>
                ❌ מודעות שנכשלו
              </h2>
              <span style={{
                background: colors.accentDanger,
                color: '#fff',
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: 600,
              }}>
                {failedListings.length}
              </span>
            </div>
            <button
              onClick={handleRetryAll}
              disabled={retrying}
              style={{
                padding: '10px 24px',
                background: retrying ? colors.bgTertiary : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: retrying ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: retrying ? 0.7 : 1,
              }}
            >
              {retrying ? (
                <><span className="spinner-border spinner-border-sm" /> מנסה שוב...</>
              ) : (
                <>🔄 נסה שוב הכל ({failedListings.length})</>
              )}
            </button>
          </div>

          {/* Retry Result */}
          {retryResult && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '16px',
              borderRadius: '8px',
              background: retryResult.errors.length > 0 ? 'rgba(255, 168, 0, 0.1)' : 'rgba(27, 197, 189, 0.1)',
              border: `1px solid ${retryResult.errors.length > 0 ? 'rgba(255, 168, 0, 0.3)' : 'rgba(27, 197, 189, 0.3)'}`,
              fontSize: '13px',
            }}>
              <strong>{retryResult.retried} מודעות נשלחו לניסיון חוזר</strong>
              {retryResult.errors.length > 0 && (
                <div style={{marginTop: '8px', color: colors.accentDanger}}>
                  {retryResult.errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}
            </div>
          )}

          {/* Failed Listings Table */}
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '40px 2fr 1.5fr 1fr 1fr 2fr 70px',
              padding: '12px 20px',
              borderBottom: `1px solid ${colors.borderColor}`,
              fontSize: '11px',
              fontWeight: 700,
              color: colors.textMuted,
              letterSpacing: '0.5px',
            }}>
              <div></div>
              <div>כותרת</div>
              <div>חשבון</div>
              <div>סוג</div>
              <div>ניסיונות</div>
              <div>שגיאה</div>
              <div></div>
            </div>

            {failedListings.map((listing) => (
              <div
                key={listing._id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 2fr 1.5fr 1fr 1fr 2fr 70px',
                  padding: '12px 20px',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                  fontSize: '13px',
                }}
              >
                {/* Thumbnail */}
                <div>
                  {listing.photo_urls[0] ? (
                    <img
                      src={toCdnUrl(listing.photo_urls[0])}
                      alt=""
                      style={{width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover'}}
                    />
                  ) : (
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '4px',
                      background: '#e4e6ef', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '14px',
                    }}>📷</div>
                  )}
                </div>

                {/* Title + Price */}
                <div>
                  <div style={{fontWeight: 600, color: colors.textPrimary, marginBottom: '2px'}}>
                    {listing.title || 'ללא כותרת'}
                  </div>
                  <div style={{fontSize: '11px', color: colors.textMuted}}>
                    ₪{listing.price?.toLocaleString() || 0}
                  </div>
                </div>

                {/* Account */}
                <div>
                  <div style={{fontWeight: 500, color: colors.textPrimary}}>
                    {listing.account_name || '—'}
                  </div>
                  <div style={{fontSize: '11px', color: colors.textMuted}}>
                    {listing.account_email}
                  </div>
                </div>

                {/* Type */}
                <div style={{color: colors.textSecondary}}>
                  {{item: 'פריט', vehicle: 'רכב', home_rent: 'השכרה', rental: 'השכרה', home_sale: 'מכירה'}[listing.listing_type] || listing.listing_type}
                </div>

                {/* Retry Count */}
                <div style={{
                  color: listing.retry_count > 2 ? colors.accentDanger : colors.textSecondary,
                  fontWeight: listing.retry_count > 2 ? 600 : 400,
                }}>
                  {listing.retry_count}
                </div>

                {/* Error */}
                <div style={{
                  fontSize: '11px',
                  color: colors.accentDanger,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }} title={listing.error_message}>
                  {listing.error_message || '—'}
                </div>

                {/* Retry */}
                <div style={{textAlign: 'center'}}>
                  {retryingOne[listing._id] === 'success' ? (
                    <span style={{color: colors.accentSuccess, fontSize: '12px', fontWeight: 600}}>נשלח</span>
                  ) : retryingOne[listing._id] === 'error' ? (
                    <span style={{color: colors.accentDanger, fontSize: '12px', fontWeight: 600}}>נכשל</span>
                  ) : (
                    <button
                      onClick={() => handleRetrySingle(listing._id)}
                      disabled={retryingOne[listing._id] === 'loading'}
                      style={{
                        padding: '4px 10px',
                        background: 'none',
                        border: `1px solid ${colors.accentPrimary}`,
                        borderRadius: '6px',
                        color: colors.accentPrimary,
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: retryingOne[listing._id] === 'loading' ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {retryingOne[listing._id] === 'loading' ? '...' : 'נסה שוב'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default MarketplacePage
